/**
 * ChatSlashDropdown — presentational "/" command dropdown for the
 * chat composer (PROD-2b). Pure props in/out; HomePage.tsx owns the
 * match detection, manifest fetch, filtering, and keyboard handling
 * (mirrors `handleKeyDown`'s existing Enter-to-send precedent) and
 * just renders whatever it's given.
 *
 * Selecting an item is a *composition* aid, not a dispatch: it
 * inserts the tool's title as a natural-language phrase and leaves
 * the message in the textarea for the user to finish and send
 * through the normal agent loop — this stays consistent with "chat
 * talks to the agent," unlike ⌘K's CommandPalette (PROD-2), which
 * invokes tools directly over REST.
 */
import type { ToolManifestItem } from "@/api";
import { cn } from "@/lib/cn";

/** Max rows shown at once — the composer sits at the bottom of the
 *  viewport, so an unbounded list would cover the conversation. */
export const SLASH_MAX_ITEMS = 8;

/**
 * The composer's "/" query, or `null` when the dropdown should stay
 * shut. Active only while the *whole* composer is a bare `/word` —
 * matches the Discord/Slack convention of a slash command at the
 * start of a message, and avoids false-triggering mid-sentence on a
 * literal "/" (URLs, dates, "and/or").
 */
export function slashQuery(inputText: string): string | null {
  const m = /^\/(\S*)$/.exec(inputText);
  return m ? m[1] : null;
}

/** Tools matching `query` (empty query → the first page). Matches on
 *  id, title, or description so `/time`, `/get_time`, and `/current`
 *  all reach `core.get_time`. */
export function filterSlashItems(
  tools: ToolManifestItem[],
  query: string,
): ToolManifestItem[] {
  const q = query.toLowerCase();
  if (!q) return tools.slice(0, SLASH_MAX_ITEMS);
  return tools
    .filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q),
    )
    .slice(0, SLASH_MAX_ITEMS);
}

export function ChatSlashDropdown({
  items,
  activeIdx,
  onHover,
  onSelect,
}: {
  items: ToolManifestItem[];
  activeIdx: number;
  onHover: (idx: number) => void;
  onSelect: (tool: ToolManifestItem) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Tool commands"
      className="max-h-56 overflow-y-auto border border-border bg-bg-elevated shadow-lg"
    >
      {items.map((tool, idx) => (
        <button
          key={tool.id}
          type="button"
          role="option"
          aria-selected={idx === activeIdx}
          onMouseDown={(e) => {
            // mousedown (not click) fires before the textarea blurs.
            e.preventDefault();
            onSelect(tool);
          }}
          onMouseEnter={() => onHover(idx)}
          className={cn(
            "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
            idx === activeIdx ? "bg-accent-bg text-fg" : "text-fg hover:bg-bg-sunken",
          )}
        >
          <span className="font-mono text-xs text-fg-faint">/{tool.id}</span>
          <span className="flex-1 truncate text-xs text-fg-faint">{tool.description}</span>
        </button>
      ))}
    </div>
  );
}
